import { request } from "../client";

import type {
  DictionaryWord,
  AddWordRequest,
  DictionaryWordsListRequest,
  DictionaryWordsListResponse,
  StudySessionRequest,
  StudySessionResponse,
  SubmitStudyResultsRequest,
  SubmitStudyResultsResponse,
  DictionaryStats,
  GetAdaptiveQuestionRequest,
  AdaptiveQuestionResponse,
  SubmitAdaptiveResultRequest,
  AdaptiveResultResponse,
} from "../types/dictionary";

export interface DictionaryAddWordRequest {
  dictationSessionId?: string;
  textId: string;
  originalWord: string;
  targetLanguage: string;
  wordPosition: number;
}

export interface DictionaryWordResponse {
  id: string;
  originalWord: string;
  translation: string;
  [key: string]: unknown;
}

export interface DictionaryStatsResponse {
  totalWords: number;
  [key: string]: unknown;
}

export interface StudyResultRequest {
  wordId: string;
  isCorrect: boolean;
  timeSpent: number;
  sessionType: string;
}

export interface DictionaryListParams {
  level?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface DictionaryTranslateRequest {
  word: string;
  context?: string;
  sourceLanguage?: string;
  targetLanguage: string;
}

export interface DictionaryTranslateFromTextRequest {
  textId: string;
  word: string;
  wordPosition?: number;
  targetLanguage: string;
}

export function createDictionaryEndpoints(baseUrl: string): {
  getAdaptiveQuestion: (
    params?: GetAdaptiveQuestionRequest,
    token?: string | null,
  ) => Promise<AdaptiveQuestionResponse>;
  submitAdaptiveResult: (
    payload: SubmitAdaptiveResultRequest,
    token?: string | null,
  ) => Promise<AdaptiveResultResponse>;
  addWord: (
    payload: DictionaryAddWordRequest,
    token?: string | null,
  ) => Promise<{ success: boolean; data: DictionaryWordResponse }>;
  getUserWords: (
    filters?: DictionaryWordsListRequest,
    token?: string | null,
  ) => Promise<DictionaryWordsListResponse>;
  getWordById: (
    id: string,
    token?: string | null,
  ) => Promise<{ word: DictionaryWord }>;
  updateWord: (
    id: string,
    payload: Partial<AddWordRequest>,
    token?: string | null,
  ) => Promise<{ word: DictionaryWord }>;
  deleteWord: (
    id: string,
    token?: string | null,
  ) => Promise<{ message: string }>;
  startStudySession: (
    payload: StudySessionRequest,
    token?: string | null,
  ) => Promise<StudySessionResponse>;
  submitStudyResults: (
    payload: SubmitStudyResultsRequest,
    token?: string | null,
  ) => Promise<SubmitStudyResultsResponse>;
  getStats: (token?: string | null) => Promise<DictionaryStats>;
  list: (
    params?: DictionaryListParams,
    token?: string | null,
  ) => Promise<{ data: DictionaryWordResponse[] }>;
  remove: (
    wordId: string,
    token?: string | null,
  ) => Promise<{ success: boolean }>;
  study: (
    limit?: number,
    token?: string | null,
  ) => Promise<{ data: DictionaryWordResponse[] }>;
  submitStudy: (
    req: StudyResultRequest,
    token?: string | null,
  ) => Promise<{ data: DictionaryWordResponse }>;
  stats: (
    sourceLanguage?: string,
    token?: string | null,
  ) => Promise<{ data: DictionaryStatsResponse }>;
  translate: (
    payload: DictionaryTranslateRequest,
    token?: string | null,
  ) => Promise<{ success: boolean; data?: { translation: string } }>;
  translateFromText: (
    payload: DictionaryTranslateFromTextRequest,
    token?: string | null,
  ) => Promise<{ success: boolean; data?: { translation: string } }>;
} {
  return {
    /**
     * Получить вопрос для адаптивного изучения
     */
    getAdaptiveQuestion(
      params: GetAdaptiveQuestionRequest = {},
      token?: string | null,
    ): Promise<AdaptiveQuestionResponse> {
      return request<AdaptiveQuestionResponse>(
        baseUrl,
        "/dictionary/adaptive/question",
        {
          method: "GET",
          query: params as Record<
            string,
            string | number | boolean | undefined
          >,
          token,
        },
      );
    },

    /**
     * Отправить результат адаптивного изучения
     */
    submitAdaptiveResult(
      payload: SubmitAdaptiveResultRequest,
      token?: string | null,
    ): Promise<AdaptiveResultResponse> {
      return request<AdaptiveResultResponse>(
        baseUrl,
        "/dictionary/adaptive/result",
        {
          method: "POST",
          body: payload,
          token,
        },
      );
    },
    /**
     * Добавить слово в словарь
     */
    addWord(
      payload: DictionaryAddWordRequest,
      token?: string | null,
    ): Promise<{ success: boolean; data: DictionaryWordResponse }> {
      return request<{ success: boolean; data: DictionaryWordResponse }>(
        baseUrl,
        "/dictionary/words",
        {
          method: "POST",
          body: payload,
          token,
        },
      );
    },

    /**
     * Получить слова пользователя
     */
    getUserWords(
      filters: DictionaryWordsListRequest = {},
      token?: string | null,
    ): Promise<DictionaryWordsListResponse> {
      return request<DictionaryWordsListResponse>(
        baseUrl,
        "/dictionary/words",
        {
          method: "GET",
          query: filters as Record<
            string,
            string | number | boolean | undefined
          >,
          token,
        },
      );
    },

    /**
     * Получить слово по ID
     */
    getWordById(
      id: string,
      token?: string | null,
    ): Promise<{ word: DictionaryWord }> {
      return request<{ word: DictionaryWord }>(
        baseUrl,
        `/dictionary/words/${id}`,
        {
          method: "GET",
          token,
        },
      );
    },

    /**
     * Обновить слово
     */
    updateWord(
      id: string,
      payload: Partial<AddWordRequest>,
      token?: string | null,
    ): Promise<{ word: DictionaryWord }> {
      return request<{ word: DictionaryWord }>(
        baseUrl,
        `/dictionary/words/${id}`,
        {
          method: "PUT",
          body: payload,
          token,
        },
      );
    },

    /**
     * Удалить слово
     */
    deleteWord(
      id: string,
      token?: string | null,
    ): Promise<{ message: string }> {
      return request<{ message: string }>(baseUrl, `/dictionary/words/${id}`, {
        method: "DELETE",
        token,
      });
    },

    /**
     * Начать сессию изучения
     */
    startStudySession(
      payload: StudySessionRequest,
      token?: string | null,
    ): Promise<StudySessionResponse> {
      return request<StudySessionResponse>(baseUrl, "/dictionary/study/start", {
        method: "POST",
        body: payload,
        token,
      });
    },

    /**
     * Отправить результаты изучения
     */
    submitStudyResults(
      payload: SubmitStudyResultsRequest,
      token?: string | null,
    ): Promise<SubmitStudyResultsResponse> {
      return request<SubmitStudyResultsResponse>(
        baseUrl,
        "/dictionary/study/submit",
        {
          method: "POST",
          body: payload,
          token,
        },
      );
    },

    /**
     * Получить статистику словаря
     */
    getStats(token?: string | null): Promise<DictionaryStats> {
      return request<DictionaryStats>(baseUrl, "/dictionary/stats", {
        method: "GET",
        token,
      });
    },

    // LEGACY METHODS - for backward compatibility
    list(
      params: DictionaryListParams = {},
      token?: string | null,
    ): Promise<{ data: DictionaryWordResponse[] }> {
      const query: Record<string, string | number> = {};
      if (params.level) query.level = params.level;
      if (params.search) query.search = params.search;
      if (params.limit !== undefined) query.limit = params.limit;
      if (params.offset !== undefined) query.offset = params.offset;
      return request<{ data: DictionaryWordResponse[] }>(
        baseUrl,
        "/dictionary/words",
        {
          method: "GET",
          query,
          token,
        },
      );
    },

    remove(
      wordId: string,
      token?: string | null,
    ): Promise<{ success: boolean }> {
      return request<{ success: boolean }>(
        baseUrl,
        `/dictionary/words/${wordId}`,
        {
          method: "DELETE",
          token,
        },
      );
    },

    study(
      limit = 20,
      token?: string | null,
    ): Promise<{ data: DictionaryWordResponse[] }> {
      return request<{ data: DictionaryWordResponse[] }>(
        baseUrl,
        "/dictionary/study",
        {
          method: "GET",
          query: { limit },
          token,
        },
      );
    },

    submitStudy(
      req: StudyResultRequest,
      token?: string | null,
    ): Promise<{ data: DictionaryWordResponse }> {
      return request<{ data: DictionaryWordResponse }>(
        baseUrl,
        "/dictionary/study-result",
        {
          method: "POST",
          body: req,
          token,
        },
      );
    },

    stats(
      sourceLanguage?: string,
      token?: string | null,
    ): Promise<{ data: DictionaryStatsResponse }> {
      return request<{ data: DictionaryStatsResponse }>(
        baseUrl,
        "/dictionary/stats",
        {
          method: "GET",
          query: sourceLanguage ? { sourceLanguage } : undefined,
          token,
        },
      );
    },

    translate(
      payload: DictionaryTranslateRequest,
      token?: string | null,
    ): Promise<{ success: boolean; data?: { translation: string } }> {
      return request<{ success: boolean; data?: { translation: string } }>(
        baseUrl,
        "/dictionary/translate",
        {
          method: "POST",
          body: payload,
          token,
        },
      );
    },

    translateFromText(
      payload: DictionaryTranslateFromTextRequest,
      token?: string | null,
    ): Promise<{ success: boolean; data?: { translation: string } }> {
      return request<{ success: boolean; data?: { translation: string } }>(
        baseUrl,
        "/dictionary/translate-from-text",
        {
          method: "POST",
          body: payload,
          token,
        },
      );
    },
  };
}
